import { ChecklistFieldType } from '@cobliteam/api-lib';

import type { FormValues } from './checklistSchema';

type TemplateItem = FormValues['items'][0];
type TranslateFn = (key: string) => string;

const textItem = (title: string): TemplateItem => ({
  title,
  type: ChecklistFieldType.TEXT,
  required: false,
  shouldRequestPhotos: false,
  content: { value: null, checked: null, label: null, options: null },
});

const selectItem = (title: string, options: string[]): TemplateItem => ({
  title,
  type: ChecklistFieldType.SINGLE_SELECT,
  required: false,
  shouldRequestPhotos: false,
  content: {
    value: null,
    checked: null,
    label: null,
    options: options.map((label) => ({ label })),
  },
});

export const CHECKLIST_TEMPLATE_ID = {
  DAILY_EXIT: 'daily_exit',
  REFUELING: 'refueling',
  RETURN: 'return',
} as const;

export type ChecklistTemplateId = (typeof CHECKLIST_TEMPLATE_ID)[keyof typeof CHECKLIST_TEMPLATE_ID];

export interface ChecklistTemplate {
  id: ChecklistTemplateId;
  nameKey: string;
  descriptionKey: string;
  values: Pick<FormValues, 'name' | 'items'>;
}

export const getChecklistTemplates = (t: TranslateFn): ChecklistTemplate[] => {
  const conformidade = [t('templates.options.conformidade_ok'), t('templates.options.conformidade_nok')];
  const simNao = [t('templates.options.sim'), t('templates.options.nao')];
  const nivelCombustivel = [
    t('templates.options.fuel_full'),
    t('templates.options.fuel_3_4'),
    t('templates.options.fuel_1_2'),
    t('templates.options.fuel_1_4'),
    t('templates.options.fuel_reserve'),
  ];

  return [
    {
      id: CHECKLIST_TEMPLATE_ID.DAILY_EXIT,
      nameKey: 'templates.daily_exit',
      descriptionKey: 'templates.daily_exit_description',
      values: {
        name: t('templates.daily_exit_name'),
        items: [
          // A. Dentro do carro — desligado
          selectItem(t('templates.daily_exit_items.seatbelt'), conformidade),
          selectItem(t('templates.daily_exit_items.fire_extinguisher'), conformidade),
          selectItem(t('templates.daily_exit_items.warning_triangle'), simNao),
          selectItem(t('templates.daily_exit_items.lug_wrench'), simNao),
          selectItem(t('templates.daily_exit_items.jack'), simNao),
          selectItem(t('templates.daily_exit_items.crlv'), simNao),
          // B. Dentro do carro — ligado
          textItem(t('templates.daily_exit_items.km_atual')),
          selectItem(t('templates.daily_exit_items.fuel_level'), nivelCombustivel),
          selectItem(t('templates.daily_exit_items.headlights'), conformidade),
          selectItem(t('templates.daily_exit_items.horn'), conformidade),
          selectItem(t('templates.daily_exit_items.turn_signals'), conformidade),
          selectItem(t('templates.daily_exit_items.hazard_lights'), conformidade),
          selectItem(t('templates.daily_exit_items.parking_brake'), conformidade),
          selectItem(t('templates.daily_exit_items.brakes'), conformidade),
          selectItem(t('templates.daily_exit_items.brake_light'), conformidade),
          selectItem(t('templates.daily_exit_items.reverse_light'), conformidade),
          // C. Fora do carro
          selectItem(t('templates.daily_exit_items.body'), conformidade),
          selectItem(t('templates.daily_exit_items.cleanliness'), [
            t('templates.options.cleanliness_very_clean'),
            t('templates.options.cleanliness_clean'),
            t('templates.options.cleanliness_dirty'),
            t('templates.options.cleanliness_very_dirty'),
          ]),
          selectItem(t('templates.daily_exit_items.front_tires'), [
            t('templates.options.condition_good'),
            t('templates.options.condition_regular'),
            t('templates.options.condition_bad'),
          ]),
          selectItem(t('templates.daily_exit_items.rear_tires'), [
            t('templates.options.condition_good'),
            t('templates.options.condition_regular'),
            t('templates.options.condition_bad'),
          ]),
          selectItem(t('templates.daily_exit_items.spare_tire'), simNao),
          selectItem(t('templates.daily_exit_items.tire_pressure'), simNao),
          selectItem(t('templates.daily_exit_items.brake_fluid'), conformidade),
          selectItem(t('templates.daily_exit_items.coolant'), conformidade),
          selectItem(t('templates.daily_exit_items.engine_oil'), conformidade),
          selectItem(t('templates.daily_exit_items.windshield'), conformidade),
          selectItem(t('templates.daily_exit_items.mirrors'), conformidade),
          selectItem(t('templates.daily_exit_items.wipers'), conformidade),
          // D. Observações
          textItem(t('templates.daily_exit_items.observations')),
        ],
      },
    },
    {
      id: CHECKLIST_TEMPLATE_ID.REFUELING,
      nameKey: 'templates.refueling',
      descriptionKey: 'templates.refueling_description',
      values: {
        name: t('templates.refueling_name'),
        items: [
          textItem(t('templates.refueling_items.km_atual')),
          textItem(t('templates.refueling_items.liters')),
          selectItem(t('templates.refueling_items.fuel_type'), [
            t('templates.options.fuel_diesel'),
            t('templates.options.fuel_gasoline'),
            t('templates.options.fuel_ethanol'),
            t('templates.options.fuel_gnv'),
          ]),
          textItem(t('templates.refueling_items.station')),
          selectItem(t('templates.refueling_items.level_after'), [
            t('templates.options.fuel_full'),
            t('templates.options.fuel_3_4'),
            t('templates.options.fuel_1_2'),
            t('templates.options.fuel_1_4'),
          ]),
          textItem(t('templates.refueling_items.observations')),
        ],
      },
    },
    {
      id: CHECKLIST_TEMPLATE_ID.RETURN,
      nameKey: 'templates.return',
      descriptionKey: 'templates.return_description',
      values: {
        name: t('templates.return_name'),
        items: [
          // A. Dentro do carro
          textItem(t('templates.return_items.km_final')),
          selectItem(t('templates.return_items.fuel_level'), nivelCombustivel),
          selectItem(t('templates.return_items.interior_cleanliness'), conformidade),
          textItem(t('templates.return_items.occurrences')),
          // B. Fora do carro
          selectItem(t('templates.return_items.new_damage'), simNao),
          textItem(t('templates.return_items.damage_description')),
          selectItem(t('templates.return_items.exterior_cleanliness'), conformidade),
          selectItem(t('templates.return_items.needs_maintenance'), simNao),
          selectItem(t('templates.return_items.needs_oil_change'), simNao),
          textItem(t('templates.return_items.maintenance_description')),
        ],
      },
    },
  ];
};
